'use es6';

import { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'foundations-theming/utils';
import { ItemTypes } from '../../utils/ItemTypes';
import { useEffect } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const defaultProps = {
  accept: ItemTypes.ITEM,
  draggable: true,
  droppable: true
};
const DraggableListItemWrapper = styled.div.withConfig({
  displayName: "UIDragListItem__DraggableListItemWrapper"
})(({
  isDragging
}) => ({
  opacity: isDragging ? 0 : 1
}));
const UIDragListItem = props => {
  const {
    accept = defaultProps.accept,
    callOnOrderChangeOnDragEnd,
    direction,
    disableDropOnHover,
    dragId: __dragId,
    draggable = defaultProps.draggable,
    droppable = defaultProps.droppable,
    id,
    index,
    moveItem,
    onDragChange,
    children,
    onHover
  } = props;
  const ref = useRef(null);
  const [{
    handlerId,
    highlighted,
    hovered
  }, connectDropTarget] = useDrop({
    accept,
    collect: monitor => {
      return {
        handlerId: monitor.getHandlerId(),
        highlighted: monitor.canDrop(),
        hovered: monitor.isOver()
      };
    },
    canDrop: () => {
      return droppable;
    },
    hover: (item, monitor) => {
      if (disableDropOnHover) {
        return;
      }
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = direction === 'vertical' ? (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2 : (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = direction === 'vertical' ? clientOffset.y - hoverBoundingRect.top : clientOffset.x - hoverBoundingRect.left;

      /**
       * Only perform the move when the mouse has crossed half of the items
       * height or, when horizontal, width.
       */
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      onHover(dragIndex, hoverIndex);

      /**
       * Mutate the monitor. Generally it's better to avoid mutations,
       * but it's good here for the sake of performance to avoid expensive
       * index searches.
       */
      item.index = hoverIndex;
    },
    drop: item => {
      /**
       * moveItem is called on drop in order to make the drop functionality work with keyboard controls.
       */
      if (!callOnOrderChangeOnDragEnd) {
        moveItem(item.index, index);
      }
    }
  });
  const [{
    ariaLabel,
    isDragging
  }, connectDragSource] = useDrag({
    type: accept,
    item: () => {
      return {
        ariaLabel,
        id,
        index,
        children
      };
    },
    canDrag: () => {
      return draggable;
    },
    collect: monitor => {
      return {
        isDragging: monitor.isDragging()
      };
    },
    end: item => {
      if (callOnOrderChangeOnDragEnd) {
        moveItem(item.index, index);
      }
    }
  });
  connectDragSource(connectDropTarget(ref));
  useEffect(() => {
    if (onDragChange) onDragChange(isDragging);
  }, [isDragging, onDragChange]);
  return /*#__PURE__*/_jsx(DraggableListItemWrapper, {
    "aria-label": ariaLabel,
    "aria-pressed": isDragging,
    "data-test-id": "uidraglistitem",
    "data-handler-id": handlerId,
    highlighted: highlighted,
    hovered: hovered,
    isDragging: isDragging,
    ref: ref,
    role: "button",
    tabIndex: 0,
    children: children
  });
};
export default UIDragListItem;
UIDragListItem.propTypes = {
  /**
   * Type of item that can be dropped on this drop target.
   * When using nested `UISortableList`s, the `accept` value of items in the parent `UISortableList`
   * should be different than the `accept` value of items in the child `UISortableList`.
   * See https://git.hubteam.com/HubSpot/ui-addon-draggable/pull/285 for context.
   */
  accept: PropTypes.string,
  /**
   * When set to `true`, the `onOrderChange` callback will be called when the drag ends
   * instead of when a drop target detects an item being dropped over it.
   */
  callOnOrderChangeOnDragEnd: PropTypes.bool,
  /**
   * The children of this item.
   */
  children: PropTypes.node.isRequired,
  /**
   * The direction in which items can be dragged.
   */
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  disableDropOnHover: PropTypes.bool,
  /**
   * @deprecated This prop is deprecated. Use an `items` array containing objects with `id`s instead.
   * If you have a use case for it, please reach out in [#react-dnd-5-upgrade](slack://channel?team=T024G0P55&id=C049MVC7VT9).
   */
  dragId: PropTypes.string,
  /**
   * Whether this specific item in the list is draggable. This prevents the drag event on the item.
   */
  draggable: PropTypes.bool,
  /**
   * Whether this specific item in the list is droppable. This prevents a drop event from occurring on the item.
   */
  droppable: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  index: PropTypes.number,
  /**
   * Callback that fires when the item is dropped over another item.
   */
  moveItem: PropTypes.func,
  /**
   * Callback that fires when the item is hovered over another item.
   */
  onHover: PropTypes.func,
  /**
   * Callback that fires when the UIDragListItem's drag state changes (start or end).
   */
  onDragChange: PropTypes.func
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
UIDragListItem.defaultProps = defaultProps;